import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

/**
 * Superadmin-only: surfaces the internal marketing/pitch content library —
 * script drafts (tutorials/scripts/pitch/), companion emails
 * (tutorials/scripts/pitch/emails/), and produced marketing videos
 * (tutorials/videos/marketing/). Internal review material, not shown
 * anywhere a regular account or white-label partner can reach it.
 */
export default function MarketingAssetsPage() {
  const { user } = useAuth();

  if (user?.user_type !== "superadmin") {
    return <div style={{ padding: 25 }}>Access restricted to Super Admins.</div>;
  }

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: "0 0 6px" }}>
          Marketing Assets
        </h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          Internal pitch script library, marketing videos, and email drafts. Not visible to any regular account.
        </p>
      </div>

      <VideosSection />
      <ScriptsSection title="Pitch Scripts" endpoint="/admin/marketing/scripts" itemEndpoint="/admin/marketing/scripts" />
      <ScriptsSection title="Marketing Emails" endpoint="/admin/marketing/emails" itemEndpoint="/admin/marketing/emails" />
    </div>
  );
}

function VideosSection() {
  const [videos, setVideos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get("/admin/marketing/videos")
      .then(res => setVideos(res.data))
      .catch(() => setError("Failed to load marketing videos."));
  }, []);

  return (
    <div style={tile}>
      <div style={sectionTitle}>Marketing Videos</div>
      {error && <p style={{ color: "#dc3545", fontSize: 13 }}>{error}</p>}
      {videos === null && !error && <p style={{ color: "#888", fontSize: 13 }}>Loading…</p>}
      {videos?.length === 0 && <p style={{ color: "#888", fontSize: 13 }}>None produced yet.</p>}
      {videos?.map(v => (
        <div key={v.filename} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{v.filename}</div>
          <video src={v.url} controls style={{ width: "100%", maxWidth: 640, borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

function ScriptsSection({ title, endpoint }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null); // filename currently expanded
  const [content, setContent] = useState({}); // filename -> content

  useEffect(() => {
    axios.get(endpoint)
      .then(res => setItems(res.data))
      .catch(() => setError(`Failed to load ${title.toLowerCase()}.`));
  }, [endpoint, title]);

  const toggle = async (filename) => {
    if (open === filename) { setOpen(null); return; }
    setOpen(filename);
    if (!content[filename]) {
      const res = await axios.get(`${endpoint}/${filename}`);
      setContent(c => ({ ...c, [filename]: res.data.content }));
    }
  };

  return (
    <div style={{ ...tile, marginTop: 16 }}>
      <div style={sectionTitle}>{title}</div>
      {error && <p style={{ color: "#dc3545", fontSize: 13 }}>{error}</p>}
      {items === null && !error && <p style={{ color: "#888", fontSize: 13 }}>Loading…</p>}
      {items?.length === 0 && <p style={{ color: "#888", fontSize: 13 }}>None found.</p>}
      {items?.map(item => (
        <div key={item.filename} style={{ borderBottom: "1px solid #eee", padding: "10px 0" }}>
          <div
            onClick={() => toggle(item.filename)}
            style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>{item.title}</span>
            <span style={{ fontSize: 11, color: "#999" }}>{item.filename} · {open === item.filename ? "▲" : "▼"}</span>
          </div>
          {open === item.filename && (
            <pre style={{
              marginTop: 10, fontSize: 12, background: "#f8f9fa", padding: 12,
              borderRadius: 6, whiteSpace: "pre-wrap", maxHeight: 400, overflowY: "auto",
            }}>
              {content[item.filename] || "Loading…"}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

const tile = { background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const sectionTitle = { fontSize: 14, fontWeight: 700, color: "#2c3e50", marginBottom: 12 };
